import { useContext, useEffect, useState } from 'react';

import { getIncodeVerification } from 'PrivateApis';

import { UserContext } from 'Context/user';

import { SENTRY_TAG } from '../IncodeSDKHelpers';

import { INCODE_SESSION_VERIFICATION_STATUS, INCODE_VERIFICATION_DOCUMENTS_STATUS } from './useIncodeVerificationDocuments.constants';
import { IncodeVerificationData, IncodeVerificationDocumentsStatus } from './useIncodeVerificationDocuments.types';

const getVerificationStatus = (verification?: IncodeVerificationData): IncodeVerificationDocumentsStatus => {
  if (!verification) {
    return INCODE_VERIFICATION_DOCUMENTS_STATUS.START;
  }

  const { completedSteps = [], modules } = verification;
  const completedCount = (completedSteps ?? []).length;
  const hasPendingModule = Object.values(modules ?? {}).some((incodeModule) => incodeModule.status === 'pending');

  if (completedCount === 0) {
    return INCODE_VERIFICATION_DOCUMENTS_STATUS.START;
  }
  if (hasPendingModule) {
    return INCODE_VERIFICATION_DOCUMENTS_STATUS.CONTINUE;
  }

  return INCODE_VERIFICATION_DOCUMENTS_STATUS.COMPLETED;
};

const getLatestKeeper = (verifications: IncodeVerificationData[]) => {
  const keepers = verifications.filter((v) => v.type === 'keeper');
  if (keepers.length === 0) {
    return undefined;
  }

  return keepers.toSorted((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
};

export const INCODE_VERIFICATION_SELLER_TYPE = 'seller' as const;

interface UseIncodeVerificationDocumentsProps {
  enquiryId?: number | null;
  verificationType?: typeof INCODE_VERIFICATION_SELLER_TYPE;
}

export const useIncodeVerificationDocuments = ({
  enquiryId,
  verificationType,
}: UseIncodeVerificationDocumentsProps) => {
  const { userState: user } = useContext(UserContext);
  const authToken = user?.auth_token;

  const [status, setStatus] = useState<IncodeVerificationDocumentsStatus>(undefined);
  const [data, setData] = useState<IncodeVerificationData | undefined>(undefined);
  const [keeperData, setKeeperData] = useState<IncodeVerificationData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authToken || !enquiryId) {
      setIsLoading(false);
      setData(undefined);
      setStatus(undefined);
      setKeeperData(undefined);
      return () => {};
    }

    const abortController = new AbortController();

    const fetchDocuments = async () => {
      setIsLoading(true);

      try {
        const response = await getIncodeVerification(
          { Authorization: authToken },
          { enquiryId, ...(verificationType && { type: verificationType }) },
          abortController,
        );

        if (abortController.signal.aborted) {
          return;
        }

        const verifications: IncodeVerificationData[] = response?.data || [];

        const sellerVerification = verifications.find(
          (v) => v.type === INCODE_VERIFICATION_SELLER_TYPE,
        ) || verifications[0];

        const latestKeeper = getLatestKeeper(verifications);
        const finalStatus = getVerificationStatus(sellerVerification);

        setKeeperData(latestKeeper);
        setData(sellerVerification);
        setStatus(finalStatus);

        setIsLoading(false);
      } catch (err) {
        if (!abortController.signal.aborted) {
          globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
          setData(undefined);
          setKeeperData(undefined);
          setStatus(INCODE_VERIFICATION_DOCUMENTS_STATUS.START);

          setIsLoading(false);
        }
      }
    };

    fetchDocuments();

    return () => {
      abortController.abort();
    };
  }, [authToken, enquiryId, verificationType]);

  const hasActiveIncodeSession = Boolean(data?.modules)
    && data?.verificationStatus === INCODE_SESSION_VERIFICATION_STATUS.PENDING;

  const hasIncodeSession = Boolean(data?.modules)
    && data?.verificationStatus !== INCODE_SESSION_VERIFICATION_STATUS.COMPLETED;

  return { data, hasActiveIncodeSession, hasIncodeSession, isLoading, keeperData, status };
};
